import { Fragment, useRef } from 'react';
import { scaleBand } from 'd3-scale';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { List } from '@mentimeter/ragnar-ui/list';
import { Legend } from '../components/Legend';
import { TextFitGhost } from '../components/text-fit-ghost/TextFitGhost';
import { VISUALIZATION_LABEL_FONTSIZES } from '../utils/constants';
import { getLongestLabel } from '../utils/get-longest-label';
import { LegendDot } from './LegendDot';

interface LegendItem {
  id: number;
  label: string;
  dotNumber: string;
  color: string;
}
interface RatingGridLegendProps extends BoxT {
  items: LegendItem[];
  onItemHover: (itemId: LegendItem['id'] | null) => void;
  itemKeyAccessor: (d: LegendItem) => number;
  maxHeight: number;
  hoveredItemKey: number | null;
}

export const RatingGridLegend = ({
  items,
  itemKeyAccessor,
  onItemHover,
  hoveredItemKey,
  maxHeight,
  maxWidth,
}: RatingGridLegendProps) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const itemYScale = scaleBand()
    .domain(items.map(itemKeyAccessor).map(String))
    .range([0, maxHeight]);
  const legendItemHeight = itemYScale.bandwidth();
  const longestLabelId = getLongestLabel(
    items,
    (d) => d.label,
    (d) => d.id?.toString() ?? '',
  );

  const hasLabelsToResize = longestLabelId !== null;

  return (
    <List
      maxWidth={maxWidth}
      justifyContent="center"
      maxHeight={maxHeight}
      height={maxHeight}
      ref={labelsContainerRef}
      gap="space1"
      style={{
        opacity: hasLabelsToResize ? 0 : 1,
        transition: 'opacity 0.5s ease',
      }}
    >
      {items.map((item, index) => {
        const isLongestLabel = item.id?.toString() === longestLabelId;
        const opacity =
          hoveredItemKey === item.id || hoveredItemKey === null ? 1 : 0.2;

        return (
          <Fragment key={item.id}>
            {index !== 0 && <Legend.ItemDivider />}
            <Legend.Item
              opacity={opacity}
              onMouseEnter={() => onItemHover(itemKeyAccessor(item))}
              onMouseLeave={() => onItemHover(null)}
              alignItems="center"
              py="min(4%, 12px)"
              width="unset"
            >
              <LegendDot
                dotNumber={item.dotNumber}
                color={item.color}
                maxHeight={legendItemHeight}
              />
              <Box
                alignItems="flex-start"
                justifyContent="flex-start"
                style={{
                  flexShrink: 1, // Prevents the legend item from overflowing the container
                  maxWidth: '100%',
                }}
              >
                {isLongestLabel && (
                  <TextFitGhost
                    width="100%"
                    height={legendItemHeight}
                    text={item.label}
                    finalFontSizeContainerRef={labelsContainerRef}
                    fontSizes={VISUALIZATION_LABEL_FONTSIZES}
                  >
                    {(ref) => (
                      <Text aria-hidden="true" ref={ref}>
                        {item.label}
                      </Text>
                    )}
                  </TextFitGhost>
                )}
                <Text fontSize="inherit">{item.label}</Text>
              </Box>
            </Legend.Item>
          </Fragment>
        );
      })}
    </List>
  );
};
